import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"

import { useNavigate } from "react-router-dom";
import CustomBackdrop from "../backdrop/CustomBackdrop";
import { useState } from "react";
import dayjs from "dayjs";

const tableStyles = {
  /* ── Header ── */
  "& .MuiDataGrid-columnHeaders": {
    borderBottom: "2px solid #e0e0e0",
    backgroundColor: "#f5f8ff",
  },

  "& .MuiDataGrid-columnHeader": {
    padding: "0 16px",
    fontWeight: 700,
    fontSize: "16px",
    color: "#1e66ab",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },

  "& .MuiDataGrid-columnHeaderTitleContainer": {
    justifyContent: "flex-start",
    padding: 0,
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    textAlign: "left",
    width: "fit-content",
    letterSpacing: "0.2px",
  },

  "& .MuiDataGrid-columnSeparator": {
    display: "none !important",
  },

  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
    outline: "none",
  },

  /* ── Cells ── */
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "0 16px",
    whiteSpace: "normal",
    color: "#1e66ab",
    fontSize: "14px",
    borderBottom: "1px solid #f0f0f0",
  },

  "& .MuiDataGrid-cell:focus": {
    outline: "none",
  },

  /* ── Checkbox ── */
  "& .MuiDataGrid-columnHeaderCheckbox": {
    padding: "0 !important",
    width: "48px !important",
    minWidth: "48px !important",
    maxWidth: "48px !important",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  "& .MuiDataGrid-cellCheckbox": {
    padding: "0 !important",
    width: "48px !important",
    minWidth: "48px !important",
    maxWidth: "48px !important",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  /* ── Rows ── */
  "& .MuiDataGrid-row": {
    borderLeft: "4px solid transparent",
    backgroundColor: "white",
    boxSizing: "border-box",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },

  "& .MuiDataGrid-row:hover": {
    backgroundColor: "#f0f4ff",
    borderLeft: "4px solid #1e66ab33",
  },

  /* ── Selected row ── */
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "rgba(150, 193, 33, 0.10) !important",
    borderLeft: "4px solid #96c121",
  },

  "& .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "rgba(150, 193, 33, 0.18) !important",
  },

  /* ── Footer ── */
  "& .MuiDataGrid-footerContainer": {
    color: "#1e66ab",
    borderTop: "2px solid #e0e0e0",
    backgroundColor: "#f5f8ff",
    minHeight: "52px",
  },
};

interface Option {
  value: string | number;
  text: string;
}

interface CustomInvoiceTableProp {
  rows: any[],
  columns: any[],
  gridHeight?: any
  selectedRows?: any,
  setSelectedRows?: any,
  checkBox?: boolean
  loading?: any
  pageSize?: number
  setSelectedInvoices?: any
  onRowClick?: (id: number) => void;
  sortOptions: Option[]
}
const CustomInvoiceTable: React.FC<CustomInvoiceTableProp> = ({ rows, columns, sortOptions = [], selectedRows, setSelectedRows, checkBox, loading, setSelectedInvoices, onRowClick }) => {
  const maxLength = 35;
  const maxTooltipLength = 500;
  const getDynamicRowId = (row: any) => row.invoiceId || row.projectId;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sortBy, setSortBy] = useState(sortOptions.length > 0 ? sortOptions[0].value : "");


  const sortedRows = [...rows].sort((a, b) => {
    const sortKey = String(sortBy);
    const parts = sortKey.split("_");
    const field = parts[0]; // e.g., "projectName", "Created date"
    const direction = parts[1] ?? "asc"; // "asc" if no direction provided

    // Special case: date field
    if (field === "Created date" || field === "Upload Date") {
      const aDate = dayjs(a[field], "DD/MM/YYYY HH:mm:ss").valueOf();
      const bDate = dayjs(b[field], "DD/MM/YYYY HH:mm:ss").valueOf();

      return direction === "asc" ? aDate - bDate : bDate - aDate;
    }

    // Default string/numeric comparison
    const aValue = a[field as keyof typeof a] ?? "";
    const bValue = b[field as keyof typeof a] ?? "";



    const result = String(aValue).localeCompare(
      String(bValue),
      undefined,
      { numeric: true, sensitivity: "base" }
    );

    return direction === "asc" ? result : -result;
  });

  const paginatedRows = sortedRows.slice(page * pageSize, (page + 1) * pageSize);
  const totalEntries = rows.length;
  const startEntry = page * pageSize + 1;
  const endEntry = Math.min((page + 1) * pageSize, totalEntries);
  const totalPages = Math.ceil(totalEntries / pageSize);

  const processedColumns = columns.map((col) => {
    if (col.renderCell) return col;

    return {
      ...col,
      renderCell: (params: any) => {
        const rawValue = params.value !== null && params.value !== undefined ? params.value : "";
        const value = String(rawValue);

        const isLongText = value.length > maxLength;
        const truncatedText = isLongText
          ? value.substring(0, maxLength) + "..."
          : value;

        const tooltipText =
          value.length > maxTooltipLength
            ? value.substring(0, maxTooltipLength) + "..."
            : value;

        return (
          <div
            title={isLongText ? tooltipText : ""} // Only show title if it's actually long
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {truncatedText}
          </div>
        );
      },
    };
  });


  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      minHeight: 0,
      height: '100%',
      width: "100%",
      bgcolor: "white",
    }}>
      <Box sx={{
        flexShrink: 0, display: "flex", justifyContent: "space-between",
        alignItems: "center", flexWrap: "wrap", mb: 2
      }}>
        <Box display="flex" alignItems="center" mb={1}>
          <span style={{ color: '#1e66ab' }}>Show</span>
          <FormControl size="small" sx={{ mx: 1, minWidth: 70 }}>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              sx={{
                fontSize: '0.75rem',
                color: 'rgba(30, 102, 171, 0.7)',
                height: '30px',
                '.MuiSelect-select': { padding: '4px 8px', minHeight: 'unset' },
              }}
            >
              {[10, 20, 50, 100].map(size => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <span style={{ color: '#1e66ab' }}>entries</span>
        </Box>

        <Box display="flex" alignItems="center" mb={1}>
          <span style={{ color: '#1e66ab' }}>Sort by</span>
          <FormControl size="small" sx={{ ml: 1, minWidth: 100 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{
                fontSize: '0.75rem',
                color: 'rgba(30, 102, 171, 0.7)',
                height: '30px',
                '.MuiSelect-select': { padding: '4px 8px', minHeight: 'unset' },
              }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <DataGrid
        sx={{ ...tableStyles, minHeight: 0,  }}
        rows={paginatedRows}
        columns={processedColumns}
        loading={loading}
        rowHeight={58}
        columnHeaderHeight={52}
        hideFooterPagination
        onRowDoubleClick={(params, event) => {
          // event.stopPropagation();
          const rowStatus = params.row.status as (number | string);
          if (rowStatus === "Forecasted") return;
          if (onRowClick) {
            onRowClick(params.id as number);
          }
        }}

        slots={{
          noRowsOverlay: () => <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%"
            }}
          >
            <Typography sx={{
              color: '#1e66ab',
              fontSize: '14px',
            }}>
              No Project invoices to display
            </Typography>
          </Box>
        }}
        checkboxSelection={checkBox}

        getRowId={getDynamicRowId}
        // onRowSelectionModelChange={(ids) => {
        //   if (!setSelectedRows) return;
        //   const selectedIDs = ids as number[];
        //   const selectedData = rows.filter((row) =>
        //     selectedIDs.includes(row.projectId)
        //   );
        //   setSelectedRows(selectedData);
        // }}
        onRowSelectionModelChange={(ids) => {
          const selectedIDs = ids as (number | string)[];

          const selectedData = rows.filter((row) =>
            selectedIDs.includes(getDynamicRowId(row))
          );

          if (setSelectedInvoices) {
            setSelectedInvoices(selectedIDs);
          }

          if (setSelectedRows) {
            setSelectedRows(selectedData);
          }
        }}
      >
      </DataGrid>

      <Box display="flex" justifyContent="space-between" mt={2} alignItems="left" flexWrap="wrap">
        <span style={{ color: '#1e66ab' }}>
          Showing {startEntry} to {endEntry} of {totalEntries} entries
        </span>
        <Box display="flex" flexWrap="wrap" mt={1} border="1px solid #ccc" borderRadius={1} overflow="hidden">
          {/* Page dropdown */}
          <FormControl size="small" sx={{ mx: 0.5, minWidth: 40, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}>
            <Select
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
              sx={{ fontSize: '0.875rem', color: '#1e66ab', '&:hover': { bgcolor: '#f0f0f0' } }}
            >
              {Array.from({ length: totalPages }, (_, i) => (
                <MenuItem key={i} value={i} sx={{ fontSize: '0.875rem' }}>{i + 1}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Previous Button */}
          <button
            style={{ border: '1px solid #ddd', padding: '5px 10px', cursor: 'pointer', backgroundColor: 'white', color: '#1e66ab', fontFamily: 'inherit' }}
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f2f2f2'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            Previous
          </button>

          {/* Page Numbers with Ellipsis */}
          {(() => {
            const totalPages = Math.ceil(totalEntries / pageSize);
            const visiblePages = 5; // Show 5 pages at a time
            let startPage = Math.max(0, page - Math.floor(visiblePages / 2));
            let endPage = Math.min(totalPages - 1, startPage + visiblePages - 1);

            if (endPage - startPage < visiblePages - 1) {
              startPage = Math.max(0, endPage - visiblePages + 1);
            }

            const pages = [];

            if (startPage > 0) {
              pages.push(
                <button
                  key={0}
                  onClick={() => setPage(0)}
                  style={{ border: '1px solid #ddd', padding: '5px 10px', backgroundColor: page === 0 ? '#1e66ab' : 'white', color: page === 0 ? 'white' : '#1e66ab', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  1
                </button>
              );
              if (startPage > 1) pages.push(<span key="left-ellipsis" style={{ padding: '5px 10px' }}>...</span>);
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  style={{ border: '1px solid #ddd', padding: '5px 10px', backgroundColor: page === i ? '#1e66ab' : 'white', color: page === i ? 'white' : '#1e66ab', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {i + 1}
                </button>
              );
            }

            if (endPage < totalPages - 1) {
              if (endPage < totalPages - 2) pages.push(<span key="right-ellipsis" style={{ padding: '5px 10px' }}>...</span>);
              pages.push(
                <button
                  key={totalPages - 1}
                  onClick={() => setPage(totalPages - 1)}
                  style={{ border: '1px solid #ddd', padding: '5px 10px', backgroundColor: page === totalPages - 1 ? '#1e66ab' : 'white', color: page === totalPages - 1 ? 'white' : '#1e66ab', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {totalPages}
                </button>
              );
            }

            return pages;
          })()}

          {/* Next Button */}
          <button
            style={{ border: '1px solid #ddd', padding: '5px 10px', cursor: 'pointer', backgroundColor: 'white', color: '#1e66ab', fontFamily: 'inherit' }}
            disabled={page >= Math.ceil(totalEntries / pageSize) - 1}
            onClick={() => setPage(page + 1)}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f2f2f2'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            Next
          </button>
        </Box>
      </Box>
    </Box>
  )
}
export default CustomInvoiceTable